import { useFormField } from "../../hooks/useFormField";
import { FieldHint } from "../FieldHint";
import { inputClass, labelClass, resolveFieldState } from "./fieldStyles";
import type { IntakeFormData } from "../../types/form";
import type { useStepValidation } from "../../hooks/useStepValidation";

type StringField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends string ? K : never;
}[keyof IntakeFormData];

export type Mask = "ein" | "zip" | "sic" | "year" | "phone" | "routing" | "account";

interface MaskedInputProps {
  field: StringField;
  label: string;
  validation: ReturnType<typeof useStepValidation>;
  mask: Mask;
  required?: boolean;
  placeholder?: string;
  /** Static helper text rendered between the label and the input. */
  hint?: string;
  containerClassName?: string;
  /** Optional onBlur callback fires after validation touchField. Used e.g. for zip lookup. */
  onAfterBlur?: (value: string) => void;
  /** When true, suppresses prefill and mismatch hints — use on steps where fields are never proposal-prefilled. */
  noPrefill?: boolean;
}

function applyMask(mask: Mask, raw: string): string {
  const digits = raw.replace(/\D/g, "");
  switch (mask) {
    case "ein": {
      const d = digits.slice(0, 9);
      return d.length <= 2 ? d : `${d.slice(0, 2)}-${d.slice(2)}`;
    }
    case "zip":
      return digits.slice(0, 5);
    case "sic": {
      const sic = digits.slice(0, 4).replace(/^0+/, "");
      return sic || (digits.length > 0 ? "0" : "");
    }
    case "year":
      return digits.slice(0, 4);
    case "phone": {
      const d = digits.slice(0, 10);
      if (d.length <= 3) return d;
      if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
    case "routing":
      return digits.slice(0, 9);
    case "account":
      return digits.slice(0, 17);
  }
}

const MASK_PLACEHOLDERS: Record<Mask, string> = {
  ein: "XX-XXXXXXX",
  zip: "XXXXX",
  sic: "XXXX",
  year: "YYYY",
  phone: "(XXX) XXX-XXXX",
  routing: "9 digits",
  account: "Account number",
};

const MASK_MAX_LENGTH: Record<Mask, number> = {
  ein: 10,
  zip: 5,
  sic: 4,
  year: 4,
  phone: 14,
  routing: 9,
  account: 17,
};

export function MaskedInput({
  field,
  label,
  validation,
  mask,
  required,
  placeholder,
  hint,
  containerClassName,
  onAfterBlur,
  noPrefill,
}: MaskedInputProps) {
  const f = useFormField(field, validation);
  const state = resolveFieldState({
    error: f.error,
    isMismatch: noPrefill ? false : !!f.mismatch,
    isPrefilled: noPrefill ? false : f.isPrefilled,
  });

  return (
    <div className={containerClassName}>
      {label && (
        <label htmlFor={field} className={labelClass()}>
          {label}
          {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
        </label>
      )}
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}
      <input
        id={field}
        type="text"
        inputMode="numeric"
        value={f.value}
        onChange={(e) => {
          const masked = applyMask(mask, e.target.value);
          f.setValue(masked as IntakeFormData[typeof field]);
        }}
        onBlur={() => {
          f.onBlur();
          onAfterBlur?.(String(f.value));
        }}
        placeholder={placeholder ?? MASK_PLACEHOLDERS[mask]}
        maxLength={MASK_MAX_LENGTH[mask]}
        aria-invalid={f.error ? true : undefined}
        aria-describedby={f.error ? `${field}-hint` : undefined}
        className={inputClass(state)}
      />
      <FieldHint id={`${field}-hint`} error={f.error} isPrefilled={noPrefill ? false : f.isPrefilled} mismatch={noPrefill ? undefined : f.mismatch} />
    </div>
  );
}
